import { useEffect, useRef, useCallback } from 'react';

/**
 * Traps keyboard focus within a container element.
 * Used for modals, dialogs, and other overlay components to meet WCAG 2.1 AA.
 *
 * Features:
 * - Tab / Shift+Tab cycles through focusable elements inside the container
 * - Returns focus to the previously-focused element when unmounted
 * - Auto-focuses the container (or first focusable child) on mount
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(', ');

export function useFocusTrap(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Capture the element that had focus before the trap activated
  useEffect(() => {
    if (active) {
      triggerRef.current = document.activeElement as HTMLElement | null;
    }
  }, [active]);

  // Return focus to trigger on deactivation
  useEffect(() => {
    if (!active) return;
    return () => {
      // Restore focus when trap is deactivated
      triggerRef.current?.focus?.();
    };
  }, [active]);

  // Auto-focus the container or first focusable child
  useEffect(() => {
    if (!active || !containerRef.current) return;

    // Small delay to allow animations/rendering to settle
    const timer = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;

      // Try to focus the first focusable child; fall back to container
      const first = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (first) {
        first.focus();
      } else {
        container.focus();
      }
    });

    return () => cancelAnimationFrame(timer);
  }, [active]);

  // Trap Tab / Shift+Tab within container
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!active || e.key !== 'Tab' || !containerRef.current) return;

      const focusable = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [active],
  );

  useEffect(() => {
    if (!active) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, handleKeyDown]);

  return containerRef;
}
